import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subject, Subscription, throwError, timer} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {ReportService} from './report.service';
import {EchoService} from './echo.service';

/**
 * Single source of truth for the Sales Qualifying Agent state on the report
 * page. The SQL card (company header), the "Sales Qualify" tile in the AI
 * Agent Tool Box and the guided wizard all read from here, so a run started
 * from one place updates the others. Live updates arrive on
 * `.sql-qualification-event` (ReportChannel.{userId}); 4 s polling is the
 * fallback while a run is in progress.
 */
export type SqlQuestionId = 'q1' | 'q2' | 'q3' | 'q4';

export type SqlStepStatus = 'pending' | 'running' | 'done' | 'skipped' | 'waiting' | 'failed';

export interface SqlProgressStep {
  key: string;
  label: string;
  status: SqlStepStatus;
  detail: string | null;
  at: string | null;
}

export interface SqlQualification {
  id: number;
  search_id: number;
  search_company_id: number;
  status: 'pending' | 'processing' | 'needs_input' | 'completed' | 'failed';
  score: number | null;
  tier: 'sales_qualified' | 'nurture' | 'not_qualified' | null;
  answers: { [q in SqlQuestionId]?: any };
  user_overrides: { [q in SqlQuestionId]?: any };
  pending_questions: SqlQuestionId[];
  evidence_sources: { url: string, tier: string }[];
  tiers_used: string[];
  summary: string | null;
  progress: { steps: SqlProgressStep[], current: string | null } | null;
  /** 'user_stopped' | 'timeout' | 'interrupted' when scored on partial evidence */
  finished_early?: 'user_stopped' | 'timeout' | 'interrupted' | null;
  stop_requested_at?: string | null;
  started_at?: string | null;
  deadline_at?: string | null;
  research_seconds?: number;
  /** false on websocket events: live state only, no answers/evidence/summary */
  full?: boolean;
  error: string | null;
  generated_at: string | null;
  updated_at: string | null;
}

export interface SqlAnswers {
  q1?: string;
  q2?: { value_usd: number, offering?: string };
  q3?: string;
  q4?: string;
  clear?: string[];
}

export interface SqlState {
  /** Feature allowlist from the backend; null until the first GET answers. */
  enabled: boolean | null;
  qualification: SqlQualification | null;
  creditCost: number;
  loaded: boolean;
  loading: boolean;
  starting: boolean;
  saving: boolean;
  stopping?: boolean;
}

export interface SqlOpenRequest {
  searchId: number;
  companyId: number;
}

const EMPTY_STATE: SqlState = {enabled: null, qualification: null, creditCost: 3, loaded: false, loading: false, starting: false, saving: false};

export const SQL_QUESTIONS: { id: SqlQuestionId, label: string, help: string }[] = [
  {id: 'q1', label: 'What does the company mainly sell?', help: 'Products (software, hardware, goods) or solutions & services (consulting, managed services, implementation).'},
  {id: 'q2', label: 'Typical value of one deal with their customers (per year)?', help: 'Price point / contract value in USD, annualised. Pick the offering line the value belongs to.'},
  {id: 'q3', label: 'Is the offering customisable for each customer?', help: 'Tailoring, bespoke builds, custom development or professional services.'},
  {id: 'q4', label: 'Can they scale their sales?', help: 'Horizontally: new markets, self-serve, partners. Vertically: tiers, upsell, enterprise plans.'}
];

export const SQL_Q1_OPTIONS = [{value: 'products', label: 'Products'}, {value: 'solutions', label: 'Solutions & services'}];
export const SQL_Q3_OPTIONS = [{value: 'yes', label: 'Yes'}, {value: 'no', label: 'No'}];
export const SQL_Q4_OPTIONS = [
  {value: 'both', label: 'Both'},
  {value: 'horizontal', label: 'Horizontally'},
  {value: 'vertical', label: 'Vertically'},
  {value: 'none', label: 'Neither'}
];

export function sqlIsRunning(q: SqlQualification | null | undefined): boolean {
  return q?.status === 'pending' || q?.status === 'processing';
}

export function sqlIsStopping(q: SqlQualification | null | undefined): boolean {
  return sqlIsRunning(q) && !!q?.stop_requested_at;
}

export function sqlFinishedEarlyLabel(q: SqlQualification | null | undefined): string {
  switch (q?.finished_early) {
    case 'user_stopped':
      return 'You stopped the research early — the score uses what was gathered until then.';
    case 'timeout':
      return 'The research hit its time limit — the score uses what was gathered until then.';
    case 'interrupted':
      return 'The research was interrupted — the score uses what was gathered until then.';
    default:
      return '';
  }
}

export function sqlTierLabel(q: SqlQualification | null | undefined): string {
  switch (q?.tier) {
    case 'sales_qualified':
      return 'Sales qualified';
    case 'nurture':
      return 'Nurture';
    case 'not_qualified':
      return 'Not qualified';
    default:
      return '';
  }
}

export function sqlTierClass(q: SqlQualification | null | undefined): 'low' | 'medium' | 'high' | 'none' {
  switch (q?.tier) {
    case 'sales_qualified':
      return 'high';
    case 'nurture':
      return 'medium';
    case 'not_qualified':
      return 'low';
    default:
      return 'none';
  }
}

@Injectable({
  providedIn: 'root'
})
export class SqlQualificationService {

  private states = new Map<number, BehaviorSubject<SqlState>>();
  private polls = new Map<number, Subscription>();
  private searchIds = new Map<number, number>();
  private echoChannel: string | null = null;

  private openRequests = new Subject<SqlOpenRequest>();
  /** Emits when some component asks the wizard to open for a company. */
  readonly openRequests$ = this.openRequests.asObservable();

  constructor(
    private reportService: ReportService,
    private echoService: EchoService
  ) {
  }

  state$(companyId: number): Observable<SqlState> {
    return this.subject(companyId).asObservable();
  }

  snapshot(companyId: number): SqlState {
    return this.subject(companyId).value;
  }

  requestOpen(searchId: number, companyId: number): void {
    if (this.snapshot(companyId).enabled !== true) {
      return;
    }
    this.openRequests.next({searchId, companyId});
  }

  /** Fetch the current state once (or again) for a report company. */
  load(searchId: number, companyId: number): void {
    if (!searchId || !companyId) {
      return;
    }
    this.searchIds.set(companyId, searchId);
    this.patch(companyId, {loading: true});

    this.reportService.getSqlQualification(searchId, companyId).subscribe({
      next: (data) => {
        this.patch(companyId, {
          enabled: data.enabled !== false,
          qualification: data.qualification,
          creditCost: data.credit_cost ?? this.snapshot(companyId).creditCost,
          loaded: true,
          loading: false
        });
        this.syncPolling(companyId);
      },
      error: (error) => {
        // 403 = not on the allowlist; anything else keeps the feature hidden until a retry
        this.patch(companyId, {loaded: true, loading: false, enabled: error?.status === 403 ? false : this.snapshot(companyId).enabled});
      }
    });
  }

  /** Start (or force re-run) the agent; the caller handles 402/409 messages. */
  start(searchId: number, companyId: number, force = false): Observable<{ qualification: SqlQualification, already_qualified?: boolean }> {
    this.searchIds.set(companyId, searchId);
    this.patch(companyId, {starting: true});

    return this.reportService.startSqlQualification(searchId, force, companyId).pipe(
      tap({
        next: (data) => {
          this.patch(companyId, {qualification: data.qualification, loaded: true, starting: false});
          this.syncPolling(companyId);
        },
        error: () => this.patch(companyId, {starting: false})
      })
    );
  }

  /** Ask the running agent to stop and finish with what it gathered. */
  stop(searchId: number, companyId: number): Observable<SqlQualification> {
    this.searchIds.set(companyId, searchId);
    this.patch(companyId, {stopping: true});

    return this.reportService.stopSqlQualification(searchId, companyId).pipe(
      map(data => data.qualification as SqlQualification),
      tap({
        next: (qualification) => {
          this.patch(companyId, {qualification, loaded: true, stopping: false});
          this.syncPolling(companyId);
        },
        error: () => this.patch(companyId, {stopping: false})
      })
    );
  }

  /** Save dialog answers (free, deterministic recompute). */
  answer(searchId: number, companyId: number, answers: SqlAnswers): Observable<SqlQualification> {
    this.searchIds.set(companyId, searchId);
    this.patch(companyId, {saving: true});

    return this.reportService.answerSqlQualification(searchId, answers, companyId).pipe(
      map(data => data.qualification as SqlQualification),
      tap({
        next: (qualification) => this.patch(companyId, {qualification, loaded: true, saving: false}),
        error: () => this.patch(companyId, {saving: false})
      })
    );
  }

  /** Subscribe once to the report owner's channel for live step updates. */
  listen(userId: number, retriesLeft = 3): void {
    if (!userId) {
      return;
    }
    const channel = `ReportChannel.${userId}`;
    if (this.echoChannel === channel) {
      return;
    }

    const echo = this.echoService.getEcho();
    if (!echo) {
      if (retriesLeft > 0) {
        setTimeout(() => this.listen(userId, retriesLeft - 1), 2000);
      }
      return;
    }

    this.echoChannel = channel;
    echo.private(channel).listen('.sql-qualification-event', (event: Partial<SqlQualification>) => {
      if (!event || !event.search_company_id) {
        return;
      }
      const companyId = Number(event.search_company_id);
      const previous = this.snapshot(companyId).qualification;

      // Events carry only the live state (the full result is over the 10 KB
      // websocket limit): merge it over what we have, keeping answers/evidence.
      const merged = {
        ...(previous && previous.id === event.id ? previous : {answers: {}, user_overrides: {}, evidence_sources: [], tiers_used: [], summary: null}),
        ...event
      } as SqlQualification;

      this.patch(companyId, {qualification: merged, loaded: true});
      this.syncPolling(companyId);

      // Run finished (or failed): fetch the full result with answers + evidence.
      const searchId = Number(event.search_id ?? this.searchIds.get(companyId));
      if (event.full === false && !sqlIsRunning(merged) && searchId) {
        this.load(searchId, companyId);
      }
    });
  }

  // ------------------------------------------------------------------

  private subject(companyId: number): BehaviorSubject<SqlState> {
    let subject = this.states.get(companyId);
    if (!subject) {
      subject = new BehaviorSubject<SqlState>({...EMPTY_STATE});
      this.states.set(companyId, subject);
    }
    return subject;
  }

  private patch(companyId: number, partial: Partial<SqlState>): void {
    const subject = this.subject(companyId);
    subject.next({...subject.value, ...partial});
  }

  private syncPolling(companyId: number): void {
    const running = sqlIsRunning(this.snapshot(companyId).qualification);
    const existing = this.polls.get(companyId);

    if (running && !existing) {
      this.polls.set(companyId, timer(4000, 4000).subscribe(() => {
        const searchId = this.searchIds.get(companyId);
        if (!searchId || !sqlIsRunning(this.snapshot(companyId).qualification)) {
          this.stopPolling(companyId);
          return;
        }
        this.load(searchId, companyId);
      }));
    } else if (!running && existing) {
      this.stopPolling(companyId);
    }
  }

  private stopPolling(companyId: number): void {
    this.polls.get(companyId)?.unsubscribe();
    this.polls.delete(companyId);
  }
}
